import { useEffect, useState } from "react";
import { ChevronRight, CloudUpload, ExternalLink, FileText, LayoutList, Plus, Search, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { IncidentRegister } from "../components/records/IncidentRegister";
import { SharedDocuments } from "../components/comms/SharedDocuments";
import { SchemaRegister } from "../components/records/SchemaRegister";
import { SkillsMatrix } from "../components/records/SkillsMatrix";
import { TrainingMatrix } from "../components/records/TrainingMatrix";
import { StaffSurvey } from "../components/records/StaffSurvey";
import { toast } from "sonner";
import { api, errMsg, fmtDate, auDate, download } from "../lib/api";
import { useGet } from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { ResourceManager } from "../components/ResourceManager";
import { PageHeader, Pill, statusTone } from "../components/Common";
import { LockStar } from "../components/LockStar";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { RecordDialog } from "../components/RecordDialog";

export function SharePointPush({ entity, row }) {
  const push = async () => {
    try { const { data } = await api.post("/sharepoint/push", { entity, entity_id: row.id }); toast.success(`Pushed to SharePoint (MOCK) · sha256 ${data.sha256.slice(0, 10)}…`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <Button size="icon" variant="ghost" title="Push to SharePoint" onClick={push} data-testid={`push-sharepoint-${row.id}`}><CloudUpload className={`h-4 w-4 ${row.sharepoint_path ? "text-primary" : ""}`} /></Button>;
}

const DOC_FIELDS = [
  { key: "title", label: "Title", required: true }, { key: "category", label: "Category", type: "select", options: ["Policy", "Procedure", "Contract", "Client record", "Form / template", "Certificate", "Insurance", "Other"] },
  { key: "version", label: "Version" }, { key: "owner", label: "Document owner" },
  { key: "section", label: "Section" }, { key: "created_by_name", label: "Created by" }, { key: "amended_by", label: "Amended by" },
  { key: "creation_date", label: "Creation date", type: "date" }, { key: "last_review_date", label: "Last review date", type: "date" },
  { key: "ceo_approved_date", label: "Approved by CEO (date)", type: "date" }, { key: "qs_approved_date", label: "Approved by Q&S Committee (date)", type: "date" },
  { key: "review_date", label: "Next review due", type: "date" }, { key: "to_be_reviewed_by", label: "To be reviewed by" },
  { key: "status", label: "Status", type: "select", options: ["draft", "active", "pending review", "archived"] },
  { key: "amendments", label: "Amendments", type: "textarea" },
  { key: "access_roles", label: "Accessible to roles", type: "tags" },
  { key: "sharepoint_url", label: "SharePoint / OneDrive URL", full: true }, { key: "notes", label: "Notes", type: "textarea" },
];

const PERMS = ["view", "create", "edit", "delete"];
const ROLES = [["staff", "Staff (default)"]];

function RoleMatrix() {
  const { user } = useAuth();
  const [data] = useGet("/role-matrix");
  const [m, setM] = useState(null);
  useEffect(() => { if (data) setM(data.matrix); }, [data]);
  if (!m) return null;
  const toggle = (mod, role, p) => {
    const cur = m[mod]?.[role] || [];
    setM({ ...m, [mod]: { ...m[mod], [role]: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] } });
  };
  const save = async () => { try { await api.put("/role-matrix", { matrix: m }); toast.success("Role matrix saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-md border bg-card/60 overflow-x-auto" data-testid="role-matrix">
      <p className="p-3 text-xs text-muted-foreground">Default access for Staff who have no access profile. Owners and Entity Admins always have full access. Set up specific access profiles in Settings → Access Profiles.</p>
      <table className="w-full text-sm">
        <thead><tr className="border-b">
          <th className="text-left p-3 overline">Module</th>
          <th className="p-3 overline">Owner / Admin</th>
          {ROLES.map(([r, l]) => <th key={r} className="p-3 overline" colSpan={4}>{l}<div className="flex justify-around mt-1 normal-case tracking-normal">{PERMS.map((p) => <span key={p}>{p}</span>)}</div></th>)}
        </tr></thead>
        <tbody>
          {data.modules.map((mod) => (
            <tr key={mod} className="border-b">
              <td className="p-3 font-medium">{mod}</td>
              <td className="p-3 text-center"><Pill tone="teal">full</Pill></td>
              {ROLES.map(([r]) => PERMS.map((p) => (
                <td key={r + p} className="p-2 text-center">
                  <Checkbox disabled={user.role === "staff"} checked={(m[mod]?.[r] || []).includes(p)} onCheckedChange={() => toggle(mod, r, p)} data-testid={`matrix-${mod}-${r}-${p}`.toLowerCase().replace(/\s/g, "-")} />
                </td>
              )))}
            </tr>
          ))}
        </tbody>
      </table>
      {user.role !== "staff" && <div className="p-3 flex justify-end"><Button onClick={save} data-testid="save-role-matrix-btn">Save matrix</Button></div>}
    </div>
  );
}

function GenericRegister({ reg }) {
  const { user } = useAuth();
  const [rows, reload] = useGet(`/records/${reg.key}`, [reg.key]);
  const [edit, setEdit] = useState(null);
  const fields = [{ key: "title", label: "Title", required: true, full: true }, { key: "record_date", label: "Date", type: "date" },
    { key: "status", label: "Status", type: "select", options: ["Open", "In progress", "Closed"] }, { key: "owner_name", label: "Responsible person" },
    { key: "details", label: "Details", type: "textarea", full: true }];
  const save = async (f) => { try { if (f.id) await api.put(`/records/${reg.key}/${f.id}`, f); else await api.post(`/records/${reg.key}`, f); toast.success("Saved"); setEdit(null); reload(); } catch (e) { toast.error(errMsg(e)); throw e; } };
  const del = async (r) => { if (!window.confirm("Delete this record?")) return; try { await api.delete(`/records/${reg.key}/${r.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3" data-testid={`register-${reg.key}`}>
      <div className="flex justify-end"><Button size="sm" onClick={() => setEdit({ status: "Open" })} data-testid={`register-add-${reg.key}`}><Plus className="h-4 w-4 mr-1" />Add record</Button></div>
      <div className="rounded-md border bg-card divide-y divide-white/5">
        {(rows || []).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`record-${r.id}`}>
            <button className="min-w-0 flex-1 text-left" onClick={() => setEdit(r)}><div className="font-medium truncate">{r.title}</div><div className="text-xs text-muted-foreground truncate">{[auDate(r.record_date || r.created_at), r.owner_name].filter(Boolean).join(" · ")}</div></button>
            <Pill tone={r.status === "Closed" ? "green" : r.status === "Open" ? "amber" : "blue"}>{r.status}</Pill>
            {user?.role !== "staff" && <button onClick={() => del(r)} className="text-slate-500 hover:text-red-400" data-testid={`record-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {rows && !rows.length && <div className="p-8 text-center text-sm text-muted-foreground">No records yet. Click Add record. This register's detailed fields can be tailored to your process.</div>}
      </div>
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={`${edit.id ? "Edit" : "New"} · ${reg.label}`} fields={fields} initial={edit} onSave={save} />}
    </div>
  );
}

function AccessToggle({ reg, reload }) {
  if (!reg.access) return null;
  const set = async (v) => { await api.put(`/records/access/${reg.key}`, { ...reg.access, everyone: v }); reload(); };
  return <label className="mb-3 flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" className="accent-[#C2A462]" checked={!!reg.access.everyone} onChange={(e) => set(e.target.checked)} data-testid={`register-everyone-${reg.key}`} />All staff can open this tab (owners and admins always can)</label>;
}

const SPECIAL = {
  "incident-register-client": () => <IncidentRegister kind="client" />,
  "incident-register-staff": () => <IncidentRegister kind="staff" />,
  "shared-documents": () => <SharedDocuments scope="account" />,
  "document-register": () => (
    <ResourceManager resource="documents" label="Document" fields={DOC_FIELDS} defaults={{ status: "active" }}
      columns={[{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "version", label: "Ver" },
        { key: "section", label: "Section" }, { key: "review_date", label: "Next review", render: (r) => auDate(r.review_date) }, { key: "access_roles", label: "Access", render: (r) => (r.access_roles || []).join(", ") || "all" },
        { key: "status", label: "Status", render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
        { key: "sp", label: "SharePoint", render: (r) => (r.sharepoint_url ? <a href={r.sharepoint_url} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()} data-testid={`open-sharepoint-${r.id}`}>Open <ExternalLink className="h-3 w-3" /></a> : "—") }]}
      rowActions={(r) => <>{r.template_pdf_path && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); download(`/documents/${r.id}/template-pdf`, `${r.title}.pdf`); }} data-testid={`doc-pdf-${r.id}`}><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>}<SharePointPush entity="documents" row={r} /></>} />
  ),
  "role-matrix": () => <RoleMatrix />,
  "skills-matrix": () => <SkillsMatrix />,
  "training-matrix": () => <TrainingMatrix />,
  "staff-survey": () => <StaffSurvey />,
};

function RegisterDrawer({ regs, cur, setTab, open, setOpen }) {
  const [q, setQ] = useState("");
  const list = regs.filter((r) => r.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <SheetContent side="right" overlayClassName="bg-black/40 backdrop-blur-[2px]" className="flex w-full flex-col gap-0 border-l border-[#C2A462]/40 bg-[#0E131C]/95 p-0 shadow-[-24px_0_60px_rgba(0,0,0,0.55)] backdrop-blur-xl data-[state=open]:duration-500 sm:max-w-none sm:w-[34vw] sm:min-w-[360px] [&>button]:text-[#94A0B2] [&>button]:hover:text-[#EBD6A0]" data-testid="record-drawer">
        <div className="border-b border-[#2B3442] px-6 pb-4 pt-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C2A462]">Record Centre</div>
          <SheetTitle className="font-display text-2xl font-semibold text-[#F5F5F5]">Registers</SheetTitle>
          <SheetDescription className="text-xs text-[#94A0B2]">{regs.length} registers you can open · press Esc to close</SheetDescription>
          <div className="relative mt-4"><Search className="absolute left-3 top-2.5 h-4 w-4 text-[#6F7B8D]" /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a register..." className="h-10 w-full rounded-lg border border-[#354052] bg-[#171E29] pl-9 pr-3 text-sm text-[#F5F5F5] outline-none placeholder:text-[#6F7B8D] focus:border-[#C2A462]" data-testid="record-drawer-search" /></div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2" data-testid="record-tabs">
          {list.map((r, i) => {
            const on = cur?.key === r.key;
            return (
              <button key={r.key} onClick={() => { setTab(r.key); setOpen(false); }} style={{ animationDelay: `${60 + i * 28}ms` }}
                className={`fade-up group flex w-full items-center gap-3 border-l-[3px] px-6 py-3 text-left text-sm transition-[background-color,color,padding] duration-200 hover:bg-[#1A2230] hover:pl-7 ${on ? "border-l-[#C2A462] bg-[#1A2230] text-[#EBD6A0]" : "border-l-transparent text-slate-300"}`} data-testid={`record-tab-${r.key}`}>
                <span className="flex-1">{r.label}</span>
                <LockStar id={`register:${r.key}`} />
                <ChevronRight className={`h-4 w-4 transition-[opacity,transform] duration-200 ${on ? "text-[#C2A462] opacity-100" : "opacity-0 -translate-x-1 group-hover:translate-x-0 group-hover:opacity-60"}`} />
              </button>
            );
          })}
          {!list.length && <div className="px-6 py-4 text-xs text-muted-foreground">{regs.length ? "No register matches that search." : "You haven't been given access to any registers yet."}</div>}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export default function RecordCentre() {
  const [regs, reload] = useGet("/records/registers");
  const [tab, setTabRaw] = useState(() => localStorage.getItem("bos_record_tab"));
  const setTab = (k) => { localStorage.setItem("bos_record_tab", k); setTabRaw(k); };
  const [open, setOpen] = useState(false);
  const cur = (regs || []).find((r) => r.key === tab) || (regs || [])[0];
  return (
    <div>
      <PageHeader overline="Governance" title="Record Centre" desc="Your registers in one place. Each tab can be opened only by the people you allow." />
      <RegisterDrawer regs={regs || []} cur={cur} setTab={setTab} open={open} setOpen={setOpen} />
      <div>
        {regs && !regs.length && <div className="text-sm text-muted-foreground">You haven't been given access to any registers yet.</div>}
        {cur && <div className="min-w-0">
          <div className="mb-2 flex items-center gap-3">
            <div className="font-display text-xl font-bold" data-testid="record-tab-title">{cur.label}</div>
            <LockStar id={`register:${cur.key}`} />
            <button onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#C2A462]/60 bg-[#C2A462]/10 px-4 py-1.5 text-xs font-medium text-[#EBD6A0] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#C2A462]/20" data-testid="record-drawer-open"><LayoutList className="h-3.5 w-3.5" />Registers</button>
          </div>
          <AccessToggle reg={cur} reload={reload} />{SPECIAL[cur.key] ? SPECIAL[cur.key]() : cur.schema ? <SchemaRegister key={cur.key} reg={cur} /> : <GenericRegister reg={cur} />}
        </div>}
      </div>
    </div>
  );
}
